import React, {useCallback, useMemo, useRef, useState} from 'react';
import {navigateHook} from "../../../../hooks/use-navigate.js";
import {useQuery} from "@tanstack/react-query";
import {Template} from "../../../../components/template/template.jsx";
import Button from "../../../../components/button/button.jsx";
import {FaEraser, FaPlus, FaSearch} from "react-icons/fa";
import {Card} from "../../../../components/card/card.jsx";
import InputForm from "../../../../components/input/input-form.jsx";
import {AgGridReact} from "ag-grid-react";
import aggridUtils from "../../../../utils/aggrid-utils.js";
import animalsService from "../../../../services/web/animals-service.js";
import {useGlobalKey} from "../../../../hooks/use-global-key.js";
import SelectComponent from "../../../../components/select/select-component.jsx";

const initialFilters = {name: "", status: []}

const AnimalsList = () => {
    const navigate = navigateHook();

    const [filter, setFilter] = useState([]);
    const [filterValues, setFilterValues] = useState(initialFilters);

    const {data} = useQuery({
        queryFn: () => animalsService.list(filter),
        queryKey: ["list", {filter}]
    });

    const [columnDefs,] = useState([
        { field: "name", headerName: "Nome", sort: "asc", flex: 1, comparator: aggridUtils.nullLastComparator},
        { field: "dt_birth", headerName: "Nascimento", flex: 1, comparator: aggridUtils.agGridCompareDateTime},
        { field: "status", headerName: "Status", flex: 1, comparator: aggridUtils.nullLastComparator},
    ]);

    const gridRef = useRef(undefined);

    const defaultColDef = useMemo(() => ({resizable: false}), []);

    const apply = useCallback(() => {
        setFilter([
            {field: "name", value: filterValues.name},
            {field: "status", value: filterValues.status?.map(s => s.value)},
        ]);
    }, [filterValues])

    const clear = useCallback(() => {
        setFilterValues(initialFilters);
        setFilter([]);
    }, [])

    useGlobalKey("Enter", apply);
    useGlobalKey("Escape", clear);

    const onCellClicked = ({data, event}) => {
        navigate(`/animals/form/${data.id}`, event.ctrlKey)
    };

    return (
        <Template>
            <Template.Header>
                <Template.Title browserTitle={'Animais - Cadastro'}>Animais</Template.Title>

                <Button target={"primary"} onClick={() => navigate("/animals/form")}>
                    <FaPlus/> Adicionar animal
                </Button>
            </Template.Header>

            <Template.Body className={"space-y-2"}>
                <Card>
                    <Card.Body className={"grid grid-cols-2 gap-4"}>
                        <InputForm
                            id={"name"}
                            label={"Nome"}
                            name={"name"}
                            value={filterValues.name}
                            onChange={({target: {value}}) => setFilterValues(prev => ({...prev, name: value}))}
                        />

                        <div className="flex justify-between gap-4">
                            <SelectComponent
                                classNameWrapper={"w-full"}
                                value={filterValues.status}
                                options={[
                                    {value: "AVAILABLE", label: "Disponível"},
                                    {value: "GARNISHED", label: "Guarnecido"},
                                    {value: "INACTIVE", label: "Inativo"}
                                ]}
                                label={"Status"}
                                onChange={(value) => setFilterValues(prev => ({...prev, status: value}))}
                                isMulti
                            />

                            <div className={"flex gap-2"}>
                                <Button target={"default"} onClick={clear} title={"Limpar"}>
                                    <FaEraser />
                                </Button>
                                <Button onClick={apply} title={"Buscar"}>
                                    <FaSearch />
                                </Button>
                            </div>
                        </div>
                    </Card.Body>
                </Card>

                <Card>
                    <Card.Body>
                        <div className={"h-[calc(100vh-153px)] aggrid-row-pointer"}>
                            <AgGridReact
                                ref={gridRef}
                                rowData={data ?? []}
                                columnDefs={columnDefs}
                                defaultColDef={defaultColDef}
                                pagination
                                paginationPageSize={100}
                                paginationPageSizeSelector={[20, 50, 100, 500]}
                                animateRows={false}
                                suppressCellFocus={true}
                                onCellClicked={onCellClicked}
                                gridOptions={{localeText: aggridUtils.localization()}}
                                theme={aggridUtils.generalTheme()}
                            />
                        </div>
                    </Card.Body>
                </Card>
            </Template.Body>
        </Template>
    );
};

export default AnimalsList;